import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";

import classNames from "classnames";
import debounce from "lodash.debounce";

import { useConfig } from "@jti/app-context/useConfig";
import { useLocale } from "@jti/app-context/useLocale";
import { GoogleAPIProvider } from "@jti/fe-clients";
import type { FlotiqStorePrizeLocator } from "@jti/fe-common";
import { FlotiqApi } from "@jti/rest-api";
import { CategoryFilter } from "@jti/ui/components/CategoryFilter";

import { PrizeLocatorMap } from "./components/PrizeLocatorMap";
import { getLoaderOptions } from "./components/PrizeLocatorMap/prize-locator.config";
import { PrizeLocatorSearchBar } from "./components/PrizeLocatorSearchBar";
import { PrizesList } from "./components/PrizesList";
import { getActiveItemIndex } from "./utils/getActiveItemIndex";

import type { PrizeLocatorContextType } from "./hooks";
import { PrizeLocatorContext } from "./hooks";
import type {
  MobileDisplayType,
  PrizeLocatorCategoriesConfig,
  StoreLocatorFilter,
  StoreLocatorPosition,
} from "./PrizeLocator.types";
import {
  calculateRadius,
  getFiltersBaseOnFlotiqResponse,
  getStoresFilteredBySelectedFilters,
  getTransformedMarkers,
} from "./utils";

import styles from "./PrizeLocator.module.scss";

export const GOOGLE_DIRECTION_URL_CONST = "https://www.google.com/maps/dir/?api=1&destination=";

export type PrizeLocatorProps = {
  defaultLat: number;
  defaultLng: number;
  flotiqApiKey: string;
  flotiqContentType: string;
  filtersEnabled?: boolean;
  filtersTranslations?: PrizeLocatorCategoriesConfig[];
  activeStoreId?: string;
  context: PrizeLocatorContextType;
};

export const PrizeLocator: FC<PrizeLocatorProps> = ({
  defaultLat,
  defaultLng,
  flotiqApiKey,
  flotiqContentType,
  filtersTranslations,
  activeStoreId = "",
  context,
}) => {
  const [map, setMap] = useState<google.maps.Map>();
  const [currentStoreId, setCurrentStoreId] = useState(activeStoreId);
  const [userMarkerPosition, setUserMarkerPosition] = useState<StoreLocatorPosition>();
  const [displayType, setDisplayType] = useState<MobileDisplayType>("map");
  const [stores, setStores] = useState<FlotiqStorePrizeLocator[]>([]);
  const [filteredStores, setFilteredStores] = useState<FlotiqStorePrizeLocator[]>([]);
  const [filters, setFilters] = useState<StoreLocatorFilter>([]);
  const [activeFilters, setActiveFilters] = useState<StoreLocatorFilter>([]);
  const { FLOTIQ_API_URL } = useConfig();
  const flotiqApi = new FlotiqApi(FLOTIQ_API_URL, flotiqApiKey, flotiqContentType);
  const config = useConfig();
  const locale = useLocale();
  const isFiltersAvailable = filters.length > 0;

  const filterStores = useCallback(() => {
    const storesFilteredBySelectedFilters = isFiltersAvailable
      ? getStoresFilteredBySelectedFilters(stores, activeFilters)
      : stores;
    setFilteredStores(storesFilteredBySelectedFilters);
  }, [stores, isFiltersAvailable, activeFilters]);

  const loadMarkersFromApi = async (map: google.maps.Map) => {
    const center = map.getCenter();
    const radius = calculateRadius(map);

    if (!center || !radius) {
      return;
    }

    try {
      const response = await flotiqApi.getStores({ radius, lat: center.lat(), lng: center.lng() });
      if (!response.data) {
        return;
      }

      const transformedMarkers = getTransformedMarkers(response.data);

      if (filtersTranslations) {
        const filtersBaseOnResult = getFiltersBaseOnFlotiqResponse(transformedMarkers, filtersTranslations);
        setFilters(filtersBaseOnResult);
      }
      setStores(transformedMarkers);
    } catch (e) {
      console.error(e, "error");
    }
  };

  const debouncedLoadMarkersFromApi = useCallback(debounce(loadMarkersFromApi, 300), []);

  const onSelectStore = (storeId: string) => {
    setCurrentStoreId(storeId);
  };

  const onChangeResultType = (type: MobileDisplayType) => {
    setDisplayType(stores.length > 0 ? type : "map");
  };
  const handleFiltersChange = (key: string) => {
    const activeItemIndex = getActiveItemIndex(key, activeFilters);
    if (activeItemIndex !== -1) {
      setActiveFilters((prevState) => prevState.toSpliced(activeItemIndex, 1));
    } else {
      const activeItem = filters.find((filter) => filter.categoryKey === key);
      if (activeItem) {
        setActiveFilters((prevState) => [...prevState, activeItem]);
      }
    }
  };

  useEffect(() => {
    filterStores();
  }, [filterStores]);

  return (
    <PrizeLocatorContext.Provider value={context}>
      <GoogleAPIProvider apiKey={config.GOOGLE_MAPS_API} options={getLoaderOptions(config, locale)}>
        <div className={classNames(styles.prizeLocator)}>
          <div className={styles.searchWrapper}>
            <PrizeLocatorSearchBar
              onChangeResultType={onChangeResultType}
              count={filteredStores.length}
              map={map}
              onLoadUserLocationMarker={setUserMarkerPosition}
              loadMarkersFromApi={debouncedLoadMarkersFromApi}
            />
          </div>
          <div className={styles.filtersContainer}>
            <CategoryFilter filters={filters} activeFilters={activeFilters} onFilterClick={handleFiltersChange} />
          </div>
          <div className={styles.resultsWrapper}>
            <PrizesList
              activeStoreId={currentStoreId}
              stores={filteredStores}
              onStoreSelect={onSelectStore}
              displayType={displayType}
            />
            <div
              className={classNames(styles.mapContainer, {
                [styles.mapContainerHidden]: displayType !== "map",
              })}
            >
              <PrizeLocatorMap
                defaultLat={defaultLat}
                activeStoreId={currentStoreId}
                defaultLng={defaultLng}
                loadMarkersFromApi={debouncedLoadMarkersFromApi}
                onMapLoaded={setMap}
                onSelectStore={onSelectStore}
                userMarkerPosition={userMarkerPosition}
                stores={filteredStores}
                flotiqApi={flotiqApi}
                setStores={setStores}
              />
            </div>
          </div>
        </div>
      </GoogleAPIProvider>
    </PrizeLocatorContext.Provider>
  );
};
